import React, { useEffect, useState, useContext } from "react";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { BarChart } from '@mui/x-charts/BarChart';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { formatChartValue, formatAxisValue } from '../utils/formatUtils';
import { applySalesMultiplier } from '../utils/chartUtils';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { 
  DashboardCardTitle, 
  CHART_MARGINS, 
  MOBILE_CHART_MARGINS, 
  MODERN_CARD_STYLE, 
  CARD_HEADER_STYLE, 
  CARD_CONTENT_STYLE,
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  getImpactSeriesConfig,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme } from '@mui/material';

const capitalizeWords = (str) =>
  str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AttributesByImpactChart = () => {
  const [attributeImpact, setAttributeImpact] = useState({});
  const [topAttributesRaw, setTopAttributesRaw] = useState([]); // Store top attributes (raw form)
  const [allAttributes, setAllAttributes] = useState([]);       // All attributes (capitalized) for the Autocomplete
  const [chartAttributes, setChartAttributes] = useState([]);   // Attributes displayed on the chart (capitalized)
  const [positiveData, setPositiveData] = useState([]);
  const [negativeData, setNegativeData] = useState([]);
  const [attributeToSubjectMap, setAttributeToSubjectMap] = useState({}); // Map attributes to their parent subjects

  // Get business context
  const businessContext = useContext(BusinessContext);
  const selectedBusiness = businessContext?.selectedBusiness;
  const businessCurrencySymbol = businessContext?.currencySymbol;

  // Get context values
  const reviewsContext = useContext(ReviewsContext);
  const {
    reviewsData = [],
    selectedSubject,
    setSelectedSubject,
    selectedAttribute,
    setSelectedAttribute,
    view = "Sales",
    currencySymbol = businessCurrencySymbol || "$",
    sortOption = "total",
    selectedCategory,
    setSelectedDate
  } = reviewsContext || {};
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const MAX_ATTRIBUTES = 8;

  // Gather and sort attribute impact
  useEffect(() => {
    try {
      const impactMap = {};
      const subjectMap = {}; // Track which subject each attribute belongs to
      
      (reviewsData || []).forEach(review => {
        if (!review.analysis || review.analysis.length === 0) return;
        const rating = review.rating;

        review.analysis.forEach(item => {
          const { attribute, sentiment, attribute_impact, subject_group, category } = item;
          if (!attribute || !subject_group) return; // Skip if either attribute or subject is missing
          
          // Skip if there's a selectedCategory mismatch
          if (
            selectedCategory &&
            (!category || category.toLowerCase() !== selectedCategory.toLowerCase())
          ) {
            return;
          }
          
          // Skip if there's a selectedSubject mismatch
          if (
            selectedSubject &&
            (subject_group.toLowerCase() !== selectedSubject.toLowerCase())
          ) {
            return;
          }

          if (!impactMap[attribute]) {
            impactMap[attribute] = { positive: 0, negative: 0, totalImpact: 0 };
          }
          if (attribute_impact > 0) {
            impactMap[attribute].positive += attribute_impact;
          } else {
            impactMap[attribute].negative += attribute_impact;
          }
          impactMap[attribute].totalImpact += Math.abs(attribute_impact);
          
          // Store the parent subject for this attribute
          // Using the capitalized forms for consistency
          const capitalizedAttribute = capitalizeWords(attribute);
          const capitalizedSubject = capitalizeWords(subject_group);
          subjectMap[capitalizedAttribute] = capitalizedSubject;
        });
      });

      setAttributeImpact(impactMap);
      setAttributeToSubjectMap(subjectMap);

      // Sort keys based on the selected sort option
      const sorted = Object.keys(impactMap).sort((a, b) => {
        switch (sortOption) {
          case 'positive':
            return impactMap[b].positive - impactMap[a].positive;
          case 'negative':
            // Sort by negative impact (most negative first, so we use a - b, not b - a)
            return impactMap[a].negative - impactMap[b].negative;
          case 'total':
          default:
            return impactMap[b].totalImpact - impactMap[a].totalImpact;
        }
      });

      // Top attributes (raw, un-capitalized)
      const top = sorted.slice(0, MAX_ATTRIBUTES);
      setTopAttributesRaw(top);

      // All attributes for the Autocomplete (capitalized form)
      const all = sorted.map(a => capitalizeWords(a));
      all.sort((a, b) => a.localeCompare(b));
      setAllAttributes(all);
    } catch (error) {
      console.error(error);
    }
  }, [selectedBusiness, reviewsData, selectedSubject, selectedCategory, sortOption]);

  // Decide what goes on the chart: top 10 plus selectedAttribute (if needed)
  useEffect(() => {
    let finalRaw = [...topAttributesRaw];
    if (selectedAttribute) {
      // Find the original form of the selected attribute
      const selectedOriginal = Object.keys(attributeImpact).find(
        a => capitalizeWords(a) === selectedAttribute
      );
      if (selectedOriginal && !finalRaw.includes(selectedOriginal)) {
        finalRaw.push(selectedOriginal);
      }
    }

    const posData = finalRaw.map(a => attributeImpact[a]?.positive || 0);
    const negData = finalRaw.map(a => attributeImpact[a]?.negative || 0);

    // Apply sales multiplier
    const adjustedPos = applySalesMultiplier(posData, view, selectedBusiness?.turnover);
    const adjustedNeg = applySalesMultiplier(negData, view, selectedBusiness?.turnover);

    setPositiveData(adjustedPos);
    setNegativeData(adjustedNeg);

    // Capitalize for display
    setChartAttributes(finalRaw.map(a => capitalizeWords(a)));
  }, [topAttributesRaw, attributeImpact, selectedAttribute, view, selectedBusiness?.turnover]);

  const onAxisClick = (event, params) => {
    // Stop event propagation to prevent document click handler from clearing selections
    event.stopPropagation();
    
    setSelectedDate(null);
    const clickedIndex = params?.dataIndex;
    if (clickedIndex !== undefined) {
      const clickedAttribute = chartAttributes[clickedIndex];
      if (clickedAttribute) {
        // Check if this attribute is already selected
        if (selectedAttribute === clickedAttribute) {
          // If already selected, unselect the attribute but keep the subject
          setSelectedAttribute(null);
          return;
        }

        // When a subject is selected, the chart only shows attributes for that subject,
        // so we always want to maintain the subject selection in this case
        setSelectedAttribute(clickedAttribute);
        // We don't modify the subject selection, keeping the current context
      }
    }
  };

  const chartSx = {
    cursor: 'pointer',
    height: '100%',
    width: '100%',
    flexGrow: 1,
    '.MuiChartsAxis-label': {
      fill: '#546E7A',
      fontWeight: 500,
      fontSize: isSmallScreen ? 10 : 12
    },
    '.MuiChartsAxis-tickLabel': {
      fill: '#546E7A',
      fontSize: isSmallScreen ? 9 : 11
    }
  };

  const slotProps = {
    legend: {
      hidden: true
    },
    tooltip: {
      content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} />
    }
  };

  const margin = isSmallScreen ? MOBILE_CHART_MARGINS : CHART_MARGINS;

  return (
    <StandardChartCard 
      data-chart-type="attribute-chart"
      data-filter-exempt="true"
      title={
        selectedSubject ? `${selectedSubject} Attributes` : 
        selectedCategory ? `${selectedCategory} Attributes` : 
        'Impact by Attribute'
      }
      titleIcon={<FormatListBulletedIcon />}
      action={
        <Autocomplete
          options={allAttributes}
          sx={{ 
            width: 120, 
            ml: '10px',
            mr: '10px',
            mt: '10px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.9rem' }
              }}
              InputLabelProps={{
                style: { fontSize: '0.9rem' }
              }}
            />
          )}
          ListboxProps={{
            style: { fontSize: '0.9rem' }
          }}
          PaperProps={{
            style: { fontSize: '0.9rem' }
          }}
          onChange={(e, newValue) => {
            // Stop event propagation to prevent document click handler from clearing selections
            if (e && e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            
            if (newValue === selectedAttribute) {
              // If already selected, unselect the attribute but keep the subject
              setSelectedAttribute(null);
            } else if (newValue) {
              // Set the attribute without changing the subject
              setSelectedAttribute(newValue);
              // We maintain the current subject selection
            } else {
              // Clear attribute selection without affecting the subject
              setSelectedAttribute(null);
            }
          }}
          value={selectedAttribute || null}
          clearOnEscape
        />
      }
    >
      <BarChart
        sx={chartSx}
        onAxisClick={onAxisClick}
        xAxis={[getXAxisConfig(chartAttributes, isSmallScreen)]}
        yAxis={[getYAxisConfig(currencySymbol, view, isSmallScreen)]}
        series={getImpactSeriesConfig(positiveData, negativeData, currencySymbol, view)}
        slotProps={{
          ...slotProps,
          root: {
            'data-chart-type': 'attribute-chart',
            'data-filter-exempt': 'true'
          }
        }}
        margin={margin}
      />
    </StandardChartCard>
  );
};

export default AttributesByImpactChart;
